/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The home page.
 *
 * Replaces components/DashboardV2/Home.js — 2,725 lines in one file that drew
 * the same five KPI tiles, four status cards and one deals table for every
 * user of every tenant, with the section order written into the JSX and no way
 * to change any of it. It also fell back to hardcoded demo deals when the API
 * failed, so a broken backend looked like a working dashboard showing somebody
 * else's numbers.
 *
 * This renders whichever dashboard the server resolves for this user:
 * pinned -> role default -> industry default -> tenant default. The resolution
 * happens server-side and arrives with the dashboard attached, so the page
 * makes one request and renders, rather than fetching candidates and choosing
 * between them — which is how a home page ends up flickering between two
 * layouts on every load.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Chip, CircularProgress, IconButton, Snackbar, Tooltip, Typography,
} from '@mui/material';
import {
  AddRounded, DashboardCustomizeRounded, OpenInNewRounded, RefreshRounded,
  TuneRounded,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import colors from 'styles/tokens/colors';
import PageHeader from 'components/v2/PageHeader';
import { displayName } from 'shell-v2/greeting';
import DashboardCanvas from './DashboardCanvas';

/** What the badge says about why this dashboard was chosen. */
const SOURCE_LABEL = {
  pinned: 'Your pinned dashboard',
  role: 'Default for your role',
  industry: 'Default for your industry',
  tenant_default: 'Your organisation’s default',
};

// v2 greets the user in the page header, so the greeting widget would repeat it.
const V2_HIDDEN_KINDS = ['greeting'];

export default function HomeDashboard() {
  const { axiosInstance, isAuthReady, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const isV2 = useTheme()?.pearlUi === 'v2';

  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/analytics/home-dashboard');
      setHome(data);
      setError(null);
    } catch (err) {
      // No demo data. A dashboard showing invented numbers while the backend
      // is down is worse than a page that says the backend is down.
      setHome(null);
      setError(err?.response?.status === 403
        ? 'You do not have permission to view dashboards.'
        : 'Your dashboard could not be loaded. Check that the backend is running.');
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  // Never issue a protected request before the token handoff has completed —
  // token state updates before /verify returns.
  useEffect(() => {
    if (isAuthReady && isAuthenticated) load();
  }, [isAuthReady, isAuthenticated, load]);

  const saveLayout = useCallback(async (layouts) => {
    if (!home?.dashboard_id) return;
    try {
      await axiosInstance.patch(`/analytics/dashboards/${home.dashboard_id}`,
                                { layout: layouts });
    } catch {
      setToast({ severity: 'error', message: 'The new arrangement could not be saved.' });
    }
  }, [axiosInstance, home]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>
      </DashboardLayout>
    );
  }

  const dashboard = home?.dashboard;
  const canEdit = dashboard?.access === 'edit' || dashboard?.access === 'manage';

  // The same buttons in both looks; v2 puts them in the page header.
  const headerActions = dashboard && (
    <>
      <Tooltip title="Refresh">
        <IconButton size="small" aria-label="Refresh" onClick={() => setRefreshKey((k) => k + 1)}>
          <RefreshRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Open in Dashboards">
        <IconButton size="small" aria-label="Open in Dashboards"
                    onClick={() => navigate(`/dashboards/${dashboard.id}`)}>
          <OpenInNewRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      {canEdit && (
        <MDButton variant={editing ? 'gradient' : 'outlined'}
                  color={editing ? 'success' : 'dark'} size="small"
                  onClick={() => setEditing((e) => !e)}>
          <TuneRounded sx={{ mr: 0.5, fontSize: 18 }} />
          {editing ? 'Done' : 'Arrange'}
        </MDButton>
      )}
      <MDButton variant="outlined" color="dark" size="small"
                onClick={() => navigate('/dashboards')}>
        Change
      </MDButton>
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} sx={isV2 ? { pt: 1 } : undefined}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} action={
            <MDButton size="small" variant="text" color="dark" onClick={load}>Retry</MDButton>
          }>
            {error}
          </Alert>
        )}

        {!dashboard ? (
          <NoDashboard onBrowse={() => navigate('/dashboards')} />
        ) : (
          <>
            {isV2 ? (
              <PageHeader
                title={displayName(user) ? `Welcome back, ${displayName(user)}` : 'Welcome back'}
                summary={new Date().toLocaleDateString(undefined, {
                  weekday: 'long', day: 'numeric', month: 'long',
                })}
                actions={headerActions}
              />
            ) : (
            <Box display="flex" alignItems="center" gap={1.5} mb={2.5} flexWrap="wrap">
              <Box flex={1} minWidth={0}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h4" fontWeight={700} lineHeight={1.2} noWrap>
                    {dashboard.name}
                  </Typography>
                  {SOURCE_LABEL[home.source] && (
                    <Tooltip title={
                      home.source === 'pinned'
                        ? 'You chose this one.'
                        : 'Pin any dashboard as your home page to change this.'
                    }>
                      <Chip size="small" label={SOURCE_LABEL[home.source]}
                            sx={{ height: 20, fontSize: 11,
                                  backgroundColor: colors.background.offWhite,
                                  color: colors.text.light }} />
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {headerActions}
            </Box>
            )}

            {editing && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Drag a widget by its title bar or resize it from the corner.
                Changes apply to this dashboard for everyone who can see it.
              </Alert>
            )}

            <DashboardCanvas
              key={`${dashboard.id}-${refreshKey}`}
              dashboard={dashboard}
              editable={editing}
              onLayoutChange={saveLayout}
              hiddenKinds={isV2 ? V2_HIDDEN_KINDS : undefined}
              emptyState={<EmptyDashboard
                canEdit={canEdit}
                onOpen={() => navigate(`/dashboards/${dashboard.id}`)}
              />}
            />
          </>
        )}
      </MDBox>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

function NoDashboard({ onBrowse }) {
  return (
    <Box sx={{ p: 8, textAlign: 'center', border: '1px dashed',
               borderColor: 'divider', borderRadius: '14px' }}>
      <DashboardCustomizeRounded sx={{ fontSize: 44, opacity: 0.25, mb: 1.5 }} />
      <Typography variant="h5" fontWeight={700} gutterBottom>
        No dashboard yet
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}
                  sx={{ maxWidth: 460, mx: 'auto' }}>
        Build a report, chart it, and put that chart on a dashboard — then pin
        the dashboard here. Your administrator can also set a default for
        everyone.
      </Typography>
      <MDButton variant="gradient" color="info" onClick={onBrowse}>
        <AddRounded sx={{ mr: 0.75 }} /> Go to Dashboards
      </MDButton>
    </Box>
  );
}

function EmptyDashboard({ canEdit, onOpen }) {
  return (
    <Box sx={{ p: 8, textAlign: 'center', border: '1px dashed',
               borderColor: 'divider', borderRadius: '14px' }}>
      <DashboardCustomizeRounded sx={{ fontSize: 40, opacity: 0.25, mb: 1 }} />
      <Typography variant="h6" gutterBottom>This dashboard has no widgets</Typography>
      {canEdit && (
        <MDButton variant="gradient" color="info" sx={{ mt: 1 }} onClick={onOpen}>
          <AddRounded sx={{ mr: 0.75 }} /> Add widgets
        </MDButton>
      )}
    </Box>
  );
}
